import React, { Suspense, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@material-ui/core";

import {
  HvButton,
  HvDialogActions,
  HvDialogContent,
  HvDialogTitle,
  HvFormStatus,
  HvGlobalActions,
  HvGrid,
  HvTypography,
} from "@hitachivantara/uikit-react-core";

import {
  UsersDomain,
  NotificationTemplate,
  WorkflowActionData,
  useUsers,
  useNotificationTemplate,
  Attribute,
  ActionNames,
} from "lib/api";

import Loading from "components/layout/Loading";
import UserSearchInput from "components/workflow/UserSearchInput";
import TagList, { HvTagListValue } from "components/common/TagList";
import TemplateSelector from "components/workflow/TemplateSelector";
import TemplatePreview from "components/workflow/TemplatePreview";

import { getUserDisplayEmail, getUserDisplay } from "lib/utils/user";
import * as Enumerable from "linq-es2015";
import { validateEmail } from "lib/utils/validation";
import { DialogContentProps, userPredicate } from "../common";

import useStyles from "./styles";
import { getActionRecipientsList } from "../../Actions/utils";

const DialogContent: React.FC<DialogContentProps> = ({
  onCancel,
  onApply,
  actionType,
  initialValues,
}) => {
  const onCancelHandler = () => {
    onCancel?.();
  };

  const theme = useTheme();
  const classes = useStyles();
  const { t } = useTranslation("emailDialog");

  const { data: userData } = useUsers(
    {
      shouldRetryOnError: false,
    },
    {
      pageNr: -1,
      pageSize: -1,
      orderBy: encodeURIComponent("name,surname,email"),
      Order: "asc",
      includeGroups: "true",
      isExcludeServiceAccount: "true",
    }
  );

  const formatUser = useCallback(
    (user: Partial<UsersDomain>, notFound = false) => {
      if (user.id != null) {
        if (notFound) {
          return `${getUserDisplay(user)} (${t("recipients.userNotFound")})`;
        }

        return getUserDisplayEmail(user);
      }

      return user.email;
    },
    [t]
  );

  const readExistingRecipients = useCallback((): Array<
    HvTagListValue<Partial<UsersDomain>>
  > => {
    const list = getActionRecipientsList(initialValues, actionType);

    return list.map((user): HvTagListValue<Partial<UsersDomain>> => {
      const contactUser =
        user.id != null ? userData?.data.find((u) => u.id === user.id) : user;

      if (!contactUser) {
        return {
          id: user.email as string,
          label: formatUser(user, true),
          color: theme.hv.palette.semantic.sema9,
          value: user,
        };
      }

      return {
        id: contactUser.email as string,
        label: formatUser(contactUser),
        color:
          contactUser.id != null
            ? theme.hv.palette.semantic.sema7
            : theme.hv.palette.semantic.sema20,
        value: contactUser,
      };
    });
  }, [
    actionType,
    formatUser,
    initialValues,
    theme.hv.palette.semantic.sema20,
    theme.hv.palette.semantic.sema7,
    theme.hv.palette.semantic.sema9,
    userData?.data,
  ]);

  const [recipients, setRecipients] = useState<
    Array<HvTagListValue<Partial<UsersDomain>>>
  >(() => readExistingRecipients());

  const { data: templateData } = useNotificationTemplate("email_templates", {
    revalidateOnFocus: false,
  });
  const [templates] = useState<NotificationTemplate[] | undefined>(
    Enumerable.asEnumerable(
      templateData?.data.templates as NotificationTemplate[]
    )
      .Where((tp) => tp.active ?? true)
      .OrderBy((tp) => tp.sequence)
      .ToArray() as NotificationTemplate[]
  );

  const [template, setTemplate] = useState<NotificationTemplate | undefined>(
    templates && templates[0]
  );

  const [recipientsUntouched, setRecipientsUntouched] = useState(true);
  const setRecipientsTouched = () => {
    if (recipientsUntouched) {
      setRecipientsUntouched(false);
    }
  };

  const [previewIsVisible, setPreviewIsVisible] = useState(false);
  const [scrollToPreview, setScrollToPreview] = useState(true);

  useEffect(() => {
    if (initialValues && templates && templates.length > 1) {
      setTemplate(
        Enumerable.asEnumerable(templates).FirstOrDefault(
          (temp) => temp.id === initialValues.notification_template_id
        )
      );
    } else if (templates && templates.length === 1) {
      // if only 1 template select this
      setScrollToPreview(false);
      setTemplate(templates[0]);
      setPreviewIsVisible(true);
      if (
        initialValues?.notification_template_id != null &&
        templates[0].id !== initialValues?.notification_template_id
      ) {
        setRecipientsTouched();
      }
    } else if (!initialValues && templates) {
      // if no edit set default as preselected template
      setTemplate(
        Enumerable.asEnumerable(templates).FirstOrDefault(
          (temp) => temp.default
        ) ?? Enumerable.asEnumerable(templates).FirstOrDefault()
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const onRecipientsListChange = (
    newRecipients: Array<HvTagListValue<Partial<UsersDomain>>>
  ) => {
    setRecipientsTouched();
    setRecipients(newRecipients);
  };

  const onUserSelected = (user: UsersDomain) => {
    setRecipientsTouched();
    setRecipients([
      ...recipients,
      {
        id: user.email,
        label: formatUser(user),
        color: theme.hv.palette.semantic.sema7,
        value: user,
      },
    ]);
  };

  const onExternalSelected = (email: string) => {
    setRecipientsTouched();
    setRecipients([
      ...recipients,
      {
        id: email,
        label: email,
        color: theme.hv.palette.semantic.sema20,
        value: { email },
      },
    ]);
  };

  const validateUser = (user: UsersDomain) => {
    const duplicate =
      recipients.findIndex(
        (recipient) => recipient.value.email === user.email
      ) !== -1;
    if (duplicate) {
      return t("recipients.duplicateAddress");
    }

    return null;
  };

  const validateExternal = (email: string) => {
    const valid = validateEmail(email);
    if (!valid) {
      return t("recipients.invalidAddress");
    }

    const duplicate =
      recipients.findIndex((recipient) => recipient.value.email === email) !==
      -1;
    if (duplicate) {
      return t("recipients.duplicateAddress");
    }

    return null;
  };

  const filterPredicate = (user: UsersDomain, searchString: string) =>
    userPredicate(user, searchString) && validateUser(user) == null;

  const tagListStatus: HvFormStatus = recipientsUntouched
    ? "standBy"
    : recipients.length > 0
    ? "valid"
    : "invalid";

  const handleTemplateChange = (
    _evt: unknown,
    newTemplate?: NotificationTemplate
  ) => {
    setTemplate(newTemplate);
    setRecipientsTouched();
  };

  const onApplyHandler = () => {
    if (recipients.length > 0 && template != null && actionType != null) {
      const attributes: Record<string, Attribute> = {};
      Object.keys(actionType.attributes).forEach((key) => {
        const attribute = actionType.attributes[key];
        attributes[key] = { ...attribute };

        if (attribute.type === "text") {
          if (attribute.label.toLocaleLowerCase().indexOf("subject") !== -1) {
            attributes[key].value = template.subject;
          } else if (
            attribute.label.toLocaleLowerCase().indexOf("body") !== -1
          ) {
            attributes[key].value = template.content;
          }
        } else if (attribute.type === "array") {
          if (attribute.label.toLocaleLowerCase().indexOf("email") !== -1) {
            attributes[key].value = recipients.map(
              ({ value: { id, name, surname, email } }) => ({
                id,
                name,
                surname,
                email,
              })
            );
          }
        }
      });

      const data: WorkflowActionData = {
        continueOnError: false,
        continueOnNoResult: false,
        isInvalid: false,
        ...initialValues,

        name: actionType.action_name,
        notification_template_id: template.id,
        attributes,
        // we'll need calculate this before sending to server
        exe_order: -1,
      };

      onApply?.({
        ...initialValues,
        ...data,
      });
    }
  };

  return (
    <>
      <HvDialogTitle className={classes.dialogTitle}>
        {t("title")}
      </HvDialogTitle>
      <HvDialogContent className={classes.dialogContent}>
        <Suspense fallback={<Loading delay={1000} loadingLabel="" />}>
          <HvGrid container spacing={0}>
            <HvGrid item xs={12} className={classes.recipientsSectionTitle}>
              <HvGlobalActions
                title={t("recipients.sectionTitle")}
                variant="section"
              />
              <HvTypography variant="normalText">
                {t("recipients.sectionDescription")}
              </HvTypography>
            </HvGrid>
            <HvGrid item xs={12}>
              <UserSearchInput
                label={t("recipients.inputLabel")}
                placeholder={t("recipients.inputPlaceholder")}
                noMatchLabel={t("recipients.noMatch")}
                filterPredicate={filterPredicate}
                formatUser={formatUser}
                validateUser={validateUser}
                onUserSelected={onUserSelected}
                allowExternal
                validateExternal={validateExternal}
                onExternalSelected={onExternalSelected}
                onBlur={setRecipientsTouched}
              />
            </HvGrid>
            <HvGrid item xs={12}>
              <TagList
                required
                label={t("recipients.listLabel")}
                value={recipients}
                status={tagListStatus}
                statusMessage={t("recipients.required")}
                onChange={onRecipientsListChange}
              />
            </HvGrid>
            <HvGrid item xs={12} className={classes.templateSectionTitle}>
              <HvGlobalActions
                title={t("template.sectionTitle")}
                variant="section"
              />
              <HvTypography variant="normalText">
                {t("template.sectionDescription")}
              </HvTypography>
            </HvGrid>
            <HvGrid item xs={12}>
              <TemplateSelector
                templateType={ActionNames.NotificationEmail}
                value={template}
                previewIsVisible={previewIsVisible}
                onChange={handleTemplateChange}
                templates={templates}
                togglePreview={setPreviewIsVisible}
                label={t("template.radioGroupLabel")}
                hidePreviewTooltip={t("template.hidePreviewTooltip")}
                showPreviewTooltip={t("template.showPreviewTooltip")}
                showPreviewAriaLabel={t("template.showPreviewAriaLabel")}
              />
            </HvGrid>
            <HvGrid item xs={12}>
              <TemplatePreview
                visible={previewIsVisible}
                scroll={scrollToPreview}
                template={template}
                title={t("template.previewTitle")}
                subjectLabel={t("template.subjectLabel")}
                contentLabel={t("template.contentLabel")}
              />
            </HvGrid>
          </HvGrid>
        </Suspense>
      </HvDialogContent>
      <HvDialogActions>
        <HvButton
          category="ghost"
          onClick={onApplyHandler}
          disabled={
            recipientsUntouched || recipients.length === 0 || template == null
          }
        >
          {t("buttons.save")}
        </HvButton>
        <HvButton category="ghost" onClick={onCancelHandler}>
          {t("buttons.cancel")}
        </HvButton>
      </HvDialogActions>
    </>
  );
};

export default DialogContent;
